import React, { useState, Fragment, useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  BaseMatchState,
  BOT_DIFFICULTY,
  GAME_TYPE,
  MATCH_TYPE,
} from "../types/match";
import { cloneDeep, uniq } from "lodash";
import DartBoardIcon from "./dartBoardIcon";
import { InOut } from "../types/x01";
import styled from "@mui/styled-engine";
import colors from "../theme/colors";
import fonts from "../theme/fonts";
import { mediaQueries } from "../theme/breakpoints";
import { OnlineMatchContext } from "../contexts/OnlineMatchContext";
import { STEPPER_LABEL } from "../types/labels";
import HalvesRoundList from "./HalveRoundList";
import { setNumPlayersLocalStorage } from "../util/localStorage";

const initSteps = [
  {
    label: STEPPER_LABEL.SELECT_DART_GAME,
  },
  {
    label: STEPPER_LABEL.NUMBER_PLAYERS,
  },
];

interface StepperProps {
  matchState: BaseMatchState;
  selectGameType: Function;
  selectMatchType: Function;
  selectBotDifficulty: Function;
  selectX01Game: Function;
  resetMatchState: Function;
  startMatch: Function;
  selectX01InOut: Function;
  numPlayers: number;
  setNumPlayers: Function;
  beta?: boolean;
}

export default function OfflineGameStepper({
  matchState,
  selectGameType,
  selectMatchType,
  selectBotDifficulty,
  selectX01Game,
  resetMatchState,
  startMatch,
  selectX01InOut,
  numPlayers,
  setNumPlayers,
  beta = false,
}: StepperProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(cloneDeep(initSteps));
  const [x01Score, setX01Score] = useState(301);
  const [gameType, setGameType] = useState(matchState.gameType);
  const [showCustomX01, setShowCustomX01] = useState(false);
  const [customX01Score, setCustomX01Score] = useState(5);
  const [inOutOption, setInOutOption] = useState<InOut>(InOut.ANY_IN_ANY_OUT);
  const { handleSetOnline } = useContext(OnlineMatchContext);

  useEffect(() => {
    if (matchState.gameType !== gameType) {
      setGameType(matchState.gameType);
    }
    // eslint-disable-next-line
  }, [matchState.gameType]);

  const handleSinglePlayer = () => {
    const newSteps = [...steps, { label: STEPPER_LABEL.BOT_DIFFICULTY }];
    setSteps(uniq(newSteps));
    selectMatchType(MATCH_TYPE.BOT);
    handleNext();
  };

  const handleMultiplayer = (matchType: MATCH_TYPE) => {
    const newSteps = [...steps].filter(
      ({ label }) => label !== STEPPER_LABEL.BOT_DIFFICULTY
    );
    setSteps(uniq(newSteps));
    selectMatchType(matchType);
    handleNext();
  };

  const handleCricket = () => {
    selectGameType(GAME_TYPE.CRICKET);
    handleNext();
  };

  const handleX01 = () => {
    selectGameType(GAME_TYPE.X01);
    const newSteps = [
      ...steps,
      { label: STEPPER_LABEL.X01_SCORE },
      { label: STEPPER_LABEL.IN_OUT },
    ];
    setSteps(uniq(newSteps));
    handleNext();
  };

  const handleHalve = () => {
    selectGameType(GAME_TYPE.HALVES);
    // const newSteps = [...steps, { label: STEPPER_LABEL.ROUNDS }];
    // setSteps(uniq(newSteps));
    handleNext();
  };

  const handleBotDifficulty = (difficulty: BOT_DIFFICULTY) => {
    selectBotDifficulty(difficulty);
    handleNext();
  };

  const handleSetX01Game = (x01Game: number) => {
    setX01Score(x01Game);
    selectX01Game(x01Game);
    handleNext();
  };

  const handleSetX01InOut = (inOut: InOut) => {
    setInOutOption(inOut);
    selectX01InOut(inOut);
    handleNext();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    if (activeStep < 3) {
      setSteps((steps) =>
        uniq(
          steps.filter(
            ({ label }) =>
              label !== STEPPER_LABEL.X01_SCORE &&
              label !== STEPPER_LABEL.IN_OUT
          )
        )
      );
    }
    if (activeStep === 0) {
      selectMatchType(MATCH_TYPE.SINGLES);
    }
  };

  const handleStart = () => {
    startMatch();
    setActiveStep(0);
  };

  const handleReset = () => {
    setSteps(cloneDeep(initSteps));
    resetMatchState();
    setActiveStep(0);
  };

  const handleOnlineMatch = () => {
    handleSetOnline(true);
  };

  return (
    <Wrapper>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step key={steps[0].label}>
          <StepLabel icon={<DartBoardIcon active={activeStep === 0} />}>
            <Label active={(activeStep === 0).toString()}>
              {steps[0].label}
            </Label>
          </StepLabel>
          <StepContent>
            <Box sx={{ mb: 2 }}>
              <Box display="flex">
                {" "}
                <OptionButton
                  variant="contained"
                  onClick={handleCricket}
                  size="large"
                  sx={{ mt: 1, mr: 1 }}
                >
                  Cricket
                </OptionButton>
                <OptionButton
                  variant="contained"
                  onClick={handleX01}
                  size="large"
                  sx={{ mt: 1, mr: 1 }}
                >
                  X01
                </OptionButton>
                <OptionButton
                  variant="contained"
                  onClick={handleHalve}
                  size="large"
                  sx={{ mt: 1, mr: 1 }}
                >
                  Halve It
                </OptionButton>
                {beta && (
                  <BackButton
                    variant="contained"
                    onClick={handleOnlineMatch}
                    size="large"
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Online
                  </BackButton>
                )}
              </Box>
            </Box>
          </StepContent>
        </Step>
        <Step key={steps[1].label}>
          <StepLabel icon={<DartBoardIcon active={activeStep === 1} />}>
            <Label active={(activeStep === 1).toString()}>
              {steps[1].label}
            </Label>
          </StepLabel>
          <StepContent>
            <Box sx={{ mb: 2 }}>
              <Box display="flex">
                {gameType !== GAME_TYPE.HALVES ? (
                  <React.Fragment>
                    {" "}
                    <OptionButton
                      variant="contained"
                      onClick={() => handleMultiplayer(MATCH_TYPE.SINGLES)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Singles
                    </OptionButton>
                    <OptionButton
                      variant="contained"
                      onClick={() => handleMultiplayer(MATCH_TYPE.DOUBLES)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Doubles
                    </OptionButton>
                    <OptionButton
                      variant="contained"
                      onClick={handleSinglePlayer}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Bot
                    </OptionButton>
                    <BackButton
                      variant="contained"
                      onClick={handleBack}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </BackButton>
                  </React.Fragment>
                ) : (
                  <Box display="flex" flexDirection="column" gap="5px">
                    <CustomNumberInput
                      type="number"
                      value={numPlayers}
                      min={1}
                      onChange={(event: any) => {
                        const numPlayers: number = parseInt(
                          event?.target?.value
                        );
                        setNumPlayers(numPlayers);
                        if (!numPlayers) return;
                        setNumPlayersLocalStorage(numPlayers);
                      }}
                    />
                    <Box display="flex">
                      <OptionButton
                        variant="contained"
                        disabled={Boolean(
                          typeof numPlayers !== "number" ||
                            numPlayers < 1 ||
                            isNaN(numPlayers)
                        )}
                        onClick={() => {
                          if (isNaN(numPlayers) || numPlayers < 1) return;
                          selectMatchType(MATCH_TYPE.NO_TEAMS);
                          handleNext();
                        }}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        OK
                      </OptionButton>
                      <BackButton
                        variant="contained"
                        onClick={() => handleBack()}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </BackButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </StepContent>
        </Step>
        {matchState.matchType === MATCH_TYPE.BOT && steps.length > 2 && (
          <Step key={steps[2].label}>
            <StepLabel icon={<DartBoardIcon active={activeStep === 2} />}>
              <Label active={(activeStep === 2).toString()}>
                {steps[2].label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Box display="flex">
                  {Object.entries(BOT_DIFFICULTY).map(([key, value]) => {
                    return (
                      <OptionButton
                        key={key}
                        variant="contained"
                        onClick={() => handleBotDifficulty(value)}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {value}
                      </OptionButton>
                    );
                  })}
                  <BackButton
                    variant="contained"
                    onClick={handleBack}
                    size="large"
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </BackButton>
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}
        {matchState.gameType === GAME_TYPE.HALVES && steps.length > 2 && (
          <Step key={steps[2].label}>
            <StepLabel icon={<DartBoardIcon active={activeStep === 2} />}>
              <Label active={(activeStep === 2).toString()}>
                {steps[2].label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" flexDirection="column">
                  <HalvesRoundList />
                  <BackButton
                    variant="contained"
                    onClick={handleBack}
                    size="large"
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </BackButton>
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}

        {gameType === GAME_TYPE.X01 && steps.length > 2 && (
          <Step key={`${steps?.[steps.length - 2]?.label}_x01`}>
            <StepLabel
              icon={
                <DartBoardIcon
                  active={
                    matchState.matchType === MATCH_TYPE.BOT
                      ? activeStep === 3
                      : activeStep === 2
                  }
                />
              }
            >
              <Label
                active={(matchState.matchType === MATCH_TYPE.BOT
                  ? activeStep === 3
                  : activeStep === 2
                ).toString()}
              >
                {steps?.[steps.length - 2]?.label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                {!showCustomX01 && (
                  <Box display="flex" style={{ flexWrap: "wrap" }}>
                    <OptionButton
                      variant="contained"
                      onClick={() => handleSetX01Game(301)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      301
                    </OptionButton>
                    <OptionButton
                      variant="contained"
                      onClick={() => handleSetX01Game(501)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      501
                    </OptionButton>
                    <OptionButton
                      variant="contained"
                      onClick={() => setShowCustomX01(true)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Custom
                    </OptionButton>
                    <BackButton
                      variant="contained"
                      onClick={handleBack}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </BackButton>
                  </Box>
                )}
                {showCustomX01 && (
                  <Box display="flex" flexDirection="column" gap="5px">
                    <Box display="flex">
                      <CustomNumberInput
                        type="number"
                        value={customX01Score}
                        min={1}
                        onChange={(event: any) => {
                          const score: number = parseInt(event?.target?.value);
                          setCustomX01Score(score);
                          if (!score) return;
                        }}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        style={{
                          color: colors.lightBeige,
                          fontFamily: fonts.family.chalk,
                          fontSize: fonts.size.mediumLarge,
                        }}
                      >
                        01
                      </Box>
                    </Box>
                    <Box display="flex">
                      <OptionButton
                        variant="contained"
                        disabled={Boolean(
                          typeof customX01Score !== "number" ||
                            customX01Score < 1 ||
                            isNaN(customX01Score)
                        )}
                        onClick={() => {
                          const customScore = parseInt(`${customX01Score}01`);
                          if (isNaN(customScore)) return;
                          setX01Score(customScore);
                          handleSetX01Game(customScore);
                        }}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        OK
                      </OptionButton>
                      <BackButton
                        variant="contained"
                        onClick={() => setShowCustomX01(false)}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </BackButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </StepContent>
          </Step>
        )}
        {gameType === GAME_TYPE.X01 && steps.length > 2 && (
          <Step key={`${steps?.[steps.length - 1]?.label}_x01`}>
            <StepLabel
              icon={
                <DartBoardIcon
                  active={
                    matchState.matchType === MATCH_TYPE.BOT
                      ? activeStep === 4
                      : activeStep === 3
                  }
                />
              }
            >
              <Label
                active={(matchState.matchType === MATCH_TYPE.BOT
                  ? activeStep === 4
                  : activeStep === 3
                ).toString()}
              >
                {steps?.[steps.length - 1]?.label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" flexDirection="column">
                  <InOutWrapper>
                    {Object.entries(InOut).map(([key, value]) => {
                      return (
                        <OptionButton
                          variant="contained"
                          key={key}
                          onClick={() => handleSetX01InOut(value)}
                          size="large"
                          sx={{ mt: 1, mr: 1 }}
                          style={{ width: "100%" }}
                        >
                          <Typography
                            style={{
                              fontFamily: fonts.family.nice,
                              fontSize: fonts.size.medium,
                            }}
                          >
                            {value}
                          </Typography>
                        </OptionButton>
                      );
                    })}
                  </InOutWrapper>
                  <BackButton
                    variant="contained"
                    onClick={handleBack}
                    size="large"
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </BackButton>
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}
      </Stepper>
      {activeStep === steps.length && (
        <GameOverBox>
          <GameOverViewPaper elevation={6}>
            <GameDescriptionWrapper>
              <Box>
                {matchState.matchType === MATCH_TYPE.BOT && <Box>Bot</Box>}
                {matchState.matchType === MATCH_TYPE.SINGLES && (
                  <Box>Singles</Box>
                )}
                {matchState.matchType === MATCH_TYPE.DOUBLES && (
                  <Box>Doubles</Box>
                )}
                {matchState.matchType === MATCH_TYPE.NO_TEAMS && (
                  <Box>
                    {numPlayers} Player{numPlayers > 1 ? "s" : ""}
                  </Box>
                )}
              </Box>
              {gameType === GAME_TYPE.CRICKET && <Box>Cricket</Box>}
              {gameType === GAME_TYPE.X01 && (
                <Fragment>
                  <Box>{x01Score}</Box>
                  <Box>{inOutOption}</Box>
                </Fragment>
              )}
              {gameType === GAME_TYPE.HALVES && <Box>Halves It</Box>}
            </GameDescriptionWrapper>
            <Box display="flex" justifyContent="space-evenly">
              <PaperButton
                style={{ backgroundColor: colors.darkGray }}
                variant="contained"
                onClick={() => handleStart()}
                sx={{ mt: 1, mr: 1 }}
              >
                Start
              </PaperButton>
              <PaperButton
                style={{ backgroundColor: colors.darkRed }}
                variant="contained"
                onClick={handleBack}
                sx={{ mt: 1, mr: 1 }}
              >
                Back
              </PaperButton>
              <PaperButton
                style={{ backgroundColor: colors.darkRed }}
                variant="contained"
                onClick={handleReset}
                sx={{ mt: 1, mr: 1 }}
              >
                Reset
              </PaperButton>
            </Box>
          </GameOverViewPaper>
        </GameOverBox>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Box)({
  width: "60%",
  "@media (max-width: 650px)": {
    width: "90%",
  },
});

const OptionButton = styled(Button)({
  backgroundColor: colors.lightBeige,
  color: colors.darkGray,
  textTransform: "none",
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.nice,
  "&:hover": {
    backgroundColor: "#c7ab8d",
  },
  [mediaQueries.mobile]: {
    // fontSize: getFontSizeString(8),
    // fontSize: getFontSizeString(.9),
  },
});

const BackButton = styled(Button)({
  backgroundColor: "#881918",
  color: "white",
  textTransform: "none",
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.nice,
  "&:hover": {
    backgroundColor: "rgb(197, 98, 98)",
  },
  [mediaQueries.mobile]: {
    // fontSize: getFontSizeString(8),
    // fontSize: getFontSizeString(.9),
  },
});

const Label = styled(Typography)<{ active: string }>({
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.chalk,
  [mediaQueries.desktop]: {
    fontSize: fonts.size.large,
  },
  [mediaQueries.mobile]: {
    fontSize: fonts.size.medium,
  },
});

const GameOverBox = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  marginTop: "30px",
});

const GameOverViewPaper = styled(Paper)({
  width: "50%",
  fontSize: fonts.size.medium,
  padding: "10px",
  fontFamily: fonts.family.chalk,
  backgroundColor: colors.lightBeige,
  "@media (max-width: 650px)": {
    width: "100%",
    fontSize: fonts.size.small,
  },
});

const GameDescriptionWrapper = styled(Box)({
  fontSize: fonts.size.medium,
  display: "flex",
  flexDirection: "column",
});

const InOutWrapper = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  gap: "5px",
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const PaperButton = styled(Button)({
  fontFamily: fonts.family.nice,
  fontSize: fonts.size.medium,
  textTransform: "none",
});

const CustomNumberInput = styled("input")({
  width: "100px",
  marginRight: "5px",
  fontFamily: fonts.family.chalk,
  fontSize: fonts.size.mediumLarge,
  textAlign: "right",
});
